import { ChefHat, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ToggleButtonGroup, {
  ToggleOption,
} from '../../../components/Forms/Buttons/ToggleButtonGroup';
import InputField from '../../../components/Forms/Input/InputField';
import {
  mapKitchenSettings,
  toKitchenUpdateDTO,
} from './RestaurantSettingsMapper';
import { RestaurantKitchenCompletionMode } from './RestaurantSettingsTypes';
import RestaurantSettingsFormLayout from './RestaurantSettingsFormLayout';
import RestaurantSettingsToggle from './RestaurantSettingsToggle';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';
import useRestaurantSettingsForm from './useRestaurantSettingsForm';

interface Props {
  branchId: number;
}

const COMPLETION_MODE_OPTIONS: ToggleOption<RestaurantKitchenCompletionMode>[] = [
  { value: 'MANUAL', label: 'Manual' },
  { value: 'AUTOMATIC', label: 'Automático' },
  { value: 'TIMED', label: 'Por tiempo' },
];

const COMPLETION_MODE_HINT: Record<RestaurantKitchenCompletionMode, string> = {
  MANUAL: 'La cocina cierra cada comanda con el botón «Completar comanda».',
  AUTOMATIC:
    'La comanda se cierra sola en cuanto todos sus platos quedan listos.',
  TIMED:
    'La comanda se cierra al agotarse su tiempo estimado total, aunque falten platos por marcar.',
};

const RestaurantKitchenSettingsForm = ({ branchId }: Props) => {
  const settings = useRestaurantSettingsForm(
    branchId,
    'kitchen',
    mapKitchenSettings,
    toKitchenUpdateDTO
  );
  const { form, updateField } = settings;

  return (
    <RestaurantSettingsFormLayout
      title="Cocina"
      description="Flujo de preparación, visualización e impresión de comandas."
      loading={settings.loading}
      saving={settings.saving}
      hasData={Boolean(form)}
      alert={settings.alert}
      onCloseAlert={() => settings.setAlert(null)}
      onSave={settings.save}
      onRetry={settings.reload}
      help={<RestaurantFormHelp topic="settingsKitchen" variant="inline" />}
    >
      {form && (
        <div className="xl:col-span-2">
          <div className="mb-5 rounded-xl border border-primary/15 bg-primary/5 p-4 sm:p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="rounded-lg bg-primary/10 p-2.5 text-primary"><ChefHat className="h-5 w-5" /></span>
                <div><h3 className="font-semibold text-black dark:text-white">Estaciones y responsables</h3><p className="mt-1 text-sm text-bodydark2 dark:text-bodydark">Configure las áreas de preparación, su impresora y el personal asignado.</p></div>
              </div>
              <Link to="/restaurant/preparation-stations" className="inline-flex shrink-0 items-center justify-center gap-1 rounded-lg border border-primary px-4 py-2.5 text-sm font-medium text-primary transition hover:bg-primary hover:text-white">Administrar estaciones<ChevronRight className="h-4 w-4" /></Link>
            </div>
          </div>

          <RestaurantSettingsToggle
            label="Usar flujo de cocina"
            description="Al enviar la comanda se genera y aparece en el tablero de cocina. Desactívalo si el restaurante no prepara en cocina y la orden pasa directo a cobro."
            name="kitchen-workflow"
            checked={form.kitchenWorkflowEnabled}
            onChange={() =>
              updateField(
                'kitchenWorkflowEnabled',
                !form.kitchenWorkflowEnabled
              )
            }
          />

          {!form.kitchenWorkflowEnabled && (
            <div className="mb-5 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-xs leading-5 text-black dark:text-white">
              Con el flujo de cocina desactivado, las órdenes no se envían al
              tablero: al confirmarlas quedan listas para cobrar. El resto de
              opciones de esta sección se ignoran.
            </div>
          )}

          <div
            className={
              form.kitchenWorkflowEnabled
                ? ''
                : 'pointer-events-none select-none opacity-50'
            }
            aria-disabled={!form.kitchenWorkflowEnabled}
          >
            <div className="grid gap-x-6 md:grid-cols-2">
              <RestaurantSettingsToggle
                label="Kitchen Display"
                description="Muestra las órdenes en la pantalla de cocina."
                name="kitchen-display"
                checked={form.kitchenDisplayEnabled}
                onChange={() =>
                  updateField(
                    'kitchenDisplayEnabled',
                    !form.kitchenDisplayEnabled
                  )
                }
              />
              <RestaurantSettingsToggle
                label="Impresión automática"
                description="Imprime la comanda automáticamente al enviarla a cocina."
                name="kitchen-printer"
                checked={form.kitchenPrinterEnabled}
                onChange={() =>
                  updateField(
                    'kitchenPrinterEnabled',
                    !form.kitchenPrinterEnabled
                  )
                }
              />
              <RestaurantSettingsToggle
                label="Temporizador"
                description="Muestra la cuenta regresiva de preparación en el tablero de cocina."
                name="preparation-timer"
                checked={form.preparationTimerEnabled}
                onChange={() =>
                  updateField(
                    'preparationTimerEnabled',
                    !form.preparationTimerEnabled
                  )
                }
              />
              <RestaurantSettingsToggle
                label="Avance de platos por tiempo"
                description="Marca cada plato como listo cuando se cumple su tiempo estimado. No cierra la comanda."
                name="auto-advance-items"
                checked={form.autoAdvanceItemsByTime}
                onChange={() =>
                  updateField(
                    'autoAdvanceItemsByTime',
                    !form.autoAdvanceItemsByTime
                  )
                }
              />
              <RestaurantSettingsToggle
                label="Permitir cambiar estación"
                description="Permite transferir preparaciones entre estaciones de cocina."
                name="allow-station-transfer"
                checked={form.allowStationTransfer}
                onChange={() =>
                  updateField(
                    'allowStationTransfer',
                    !form.allowStationTransfer
                  )
                }
              />
              <RestaurantSettingsToggle
                label="Preparación paralela"
                description="Permite preparar simultáneamente productos de una misma orden."
                name="parallel-preparation"
                checked={form.parallelPreparationEnabled}
                onChange={() =>
                  updateField(
                    'parallelPreparationEnabled',
                    !form.parallelPreparationEnabled
                  )
                }
              />
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <div className="rounded-xl border border-stroke bg-gray-2 px-5 py-5 dark:border-strokedark dark:bg-meta-4/20">
                <p className="text-sm font-medium text-black dark:text-white">
                  Cierre de comanda
                </p>
                <div className="mt-3 overflow-x-auto">
                  <ToggleButtonGroup
                    value={form.orderCompletionMode}
                    options={COMPLETION_MODE_OPTIONS}
                    onChange={(value) =>
                      updateField('orderCompletionMode', value)
                    }
                  />
                </div>
                <p className="mt-2 text-xs leading-5 text-bodydark2 dark:text-bodydark">
                  {COMPLETION_MODE_HINT[form.orderCompletionMode]}
                </p>

                {form.orderCompletionMode === 'TIMED' && (
                  <div className="mt-3 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-xs leading-5 text-black dark:text-white">
                    <p>
                      Con este modo se te pedirá el{' '}
                      <strong>tiempo de preparación</strong> al crear o editar
                      cada ítem de menú. Si un ítem no lo trae, se usa el tiempo
                      predeterminado de la sucursal (Configuración → General).
                    </p>
                    <Link
                      to="/restaurant/menu/preparation-times"
                      className="mt-1.5 inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                    >
                      Configurar el tiempo de cada ítem
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-stroke bg-gray-2 px-5 py-5 dark:border-strokedark dark:bg-meta-4/20">
                <InputField
                  label="Máx. comandas en preparación"
                  iconKey="user"
                  name="maxOrdersInPreparation"
                  type="number"
                  min={0}
                  max={99}
                  value={form.maxOrdersInPreparation}
                  onChange={(event) =>
                    updateField(
                      'maxOrdersInPreparation',
                      Number(event.target.value)
                    )
                  }
                />
                <p className="mt-2 text-xs leading-5 text-bodydark2 dark:text-bodydark">
                  Cuántas comandas pasan a preparación a la vez; el resto queda
                  en cola y avanza sola al liberarse un lugar. 0 = sin límite
                  (inicio manual, como hasta ahora).
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </RestaurantSettingsFormLayout>
  );
};

export default RestaurantKitchenSettingsForm;
